import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { INDIAN_STATES } from "@/lib/states";
import { toast } from "sonner";
import {
  Mail, Lock, ArrowRight, Sparkles, Phone, IdCard, User, MapPin,
  KeyRound, RotateCcw, MessageSquare,
} from "lucide-react";

export default function Login() {
  const { login, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Business
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bLoading, setBLoading] = useState(false);

  // Customer OTP flow
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [step, setStep] = useState(1); // 1=phone, 2=otp+aadhaar4
  const [otp, setOtp] = useState("");
  const [aadhaar4, setAadhaar4] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [debugOtp, setDebugOtp] = useState("");

  const submitBusiness = async (e) => {
    e.preventDefault();
    setBLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      const target = loc.state?.from ||
        (u.role === "provider" ? "/provider" : u.role === "admin" ? "/admin" : "/browse");
      nav(target);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBLoading(false); }
  };

  const sendOtp = async () => {
    const p = phone.replace(/\D/g, "");
    if (p.length < 10) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone: p });
      setStep(2);
      if (data.debug_otp) {
        setDebugOtp(data.debug_otp);
        toast.success(`OTP sent. [DEV mode] Code: ${data.debug_otp}`, { duration: 8000 });
      } else {
        setDebugOtp("");
        toast.success("OTP sent to your phone");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setOtpLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        name: name.trim() || undefined,
        phone: phone.replace(/\D/g, ""),
        otp: otp.trim(),
        aadhaar_last4: aadhaar4.replace(/\D/g, ""),
        state: state || undefined,
      });
      await refresh();
      toast.success(
        `Welcome, ${data.user.name.split(" ")[0]}!${data.user.code ? ` Your ID: ${data.user.code}` : ""}`
      );
      nav(loc.state?.from || "/browse");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setVerifyLoading(false); }
  };

  const resetOtp = () => {
    setStep(1); setOtp(""); setAadhaar4(""); setDebugOtp("");
  };

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2" data-testid="login-page">
      <div className="order-2 hidden overflow-hidden rounded-3xl border bg-brand-ink text-white lg:order-1 lg:block">
        <div className="relative h-full p-10">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-orange"><Sparkles size={18} /></div>
          <h2 className="mt-8 font-heading text-4xl font-extrabold leading-tight tracking-tight">
            Welcome back to <br />
            <span className="text-brand-cyan">TridentDigitalPulse.</span>
          </h2>
          <p className="mt-4 max-w-sm text-white/70">
            Customers sign in with a phone OTP and the last 4 digits of Aadhaar. Providers &amp; admins use email and password.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            <li>• Secure OTP-based login</li>
            <li>• Only last 4 digits of Aadhaar — never the full number</li>
            <li>• Bookings tracked to your phone with SMS updates</li>
          </ul>
        </div>
      </div>

      <div className="order-1 lg:order-2">
        <div className="rounded-3xl border border-foreground/10 bg-white p-6 sm:p-10">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Sign in</h1>

          <Tabs defaultValue="customer" className="mt-6">
            <TabsList className="grid w-full grid-cols-2" data-testid="login-tabs">
              <TabsTrigger value="customer" data-testid="tab-customer">
                <User size={14} className="mr-1.5" /> Customer
              </TabsTrigger>
              <TabsTrigger value="business" data-testid="tab-business">
                <Mail size={14} className="mr-1.5" /> Provider / Admin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customer" className="mt-6">
              {step === 1 ? (
                <>
                  <p className="mb-5 text-sm text-muted-foreground">
                    Step 1 of 2 · We'll text you a one-time code.
                  </p>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="c-name">Your name <span className="text-muted-foreground">(new users only)</span></Label>
                      <div className="relative">
                        <User size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="c-name" value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. Priya Sharma" className="pl-9 h-11"
                          data-testid="customer-name" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="c-phone">Phone number</Label>
                      <div className="relative">
                        <Phone size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="c-phone" required inputMode="tel"
                          value={phone} onChange={(e) => setPhone(e.target.value)}
                          placeholder="10-digit mobile number" maxLength={15}
                          className="pl-9 h-11" data-testid="customer-phone" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="c-state" className="flex items-center gap-1"><MapPin size={12} /> State <span className="text-muted-foreground">(new users only)</span></Label>
                      <Select value={state} onValueChange={setState}>
                        <SelectTrigger id="c-state" data-testid="customer-state"><SelectValue placeholder="Select your state" /></SelectTrigger>
                        <SelectContent>
                          {INDIAN_STATES.map((s) => (
                            <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={sendOtp} disabled={otpLoading}
                      className="h-11 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
                      data-testid="send-otp-btn"
                    >
                      {otpLoading ? "Sending OTP..." : (<><MessageSquare size={16} /> Send OTP</>)}
                    </Button>
                    <p className="text-center text-[11px] text-muted-foreground">
                      By continuing, you agree to receive SMS updates on booking events.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-2 text-sm text-muted-foreground">
                    Step 2 of 2 · Enter the code we sent to <b>{phone}</b>
                  </p>
                  <button type="button" onClick={resetOtp} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange" data-testid="change-phone-btn">
                    <RotateCcw size={12} /> Change phone
                  </button>
                  {debugOtp && (
                    <div className="mb-4 rounded-xl border border-brand-yellow/40 bg-brand-yellow/10 p-3 text-xs">
                      <b>Twilio not configured.</b> Using dev OTP: <span className="font-mono font-bold">{debugOtp}</span>
                    </div>
                  )}
                  <form onSubmit={verifyOtp} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="c-otp">6-digit OTP</Label>
                      <div className="relative">
                        <KeyRound size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="c-otp" required inputMode="numeric"
                          value={otp} onChange={(e) => setOtp(e.target.value)}
                          placeholder="123456" maxLength={6}
                          className="pl-9 h-11 tracking-widest" data-testid="customer-otp" autoFocus />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="c-aadhaar4">Last 4 digits of Aadhaar</Label>
                      <div className="relative">
                        <IdCard size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="c-aadhaar4" required inputMode="numeric"
                          value={aadhaar4} onChange={(e) => setAadhaar4(e.target.value)}
                          placeholder="Last 4 digits" maxLength={4}
                          className="pl-9 h-11 tracking-widest" data-testid="customer-aadhaar4" />
                      </div>
                      <p className="text-[11px] text-muted-foreground">Used to verify your identity — full Aadhaar never leaves your device.</p>
                    </div>
                    <Button
                      type="submit" disabled={verifyLoading}
                      className="h-11 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
                      data-testid="verify-otp-btn"
                    >
                      {verifyLoading ? "Verifying..." : (<>Verify &amp; sign in <ArrowRight size={16} /></>)}
                    </Button>
                    <button type="button" onClick={sendOtp} disabled={otpLoading}
                      className="block w-full text-center text-xs font-semibold text-muted-foreground hover:text-brand-orange"
                      data-testid="resend-otp-btn">
                      {otpLoading ? "Resending..." : "Didn't get it? Resend OTP"}
                    </button>
                  </form>
                </>
              )}
            </TabsContent>

            <TabsContent value="business" className="mt-6">
              <p className="mb-5 text-sm text-muted-foreground">
                Provider or admin? Sign in with your email and password. New provider?{" "}
                <Link to="/register?role=provider" className="font-semibold text-brand-orange">Create an account</Link>
              </p>
              <form onSubmit={submitBusiness} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input id="email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" className="pl-9 h-11"
                      data-testid="login-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input id="password" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" className="pl-9 h-11"
                      data-testid="login-password" />
                  </div>
                </div>
                <Button type="submit" disabled={bLoading}
                  className="h-11 w-full rounded-full bg-brand-ink text-white hover:bg-brand-ink/90"
                  data-testid="login-submit">
                  {bLoading ? "Signing in..." : (<>Log in <ArrowRight size={16} /></>)}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
